// File tree from the meta doc's live entries, rendered in the vault content
// area (decisions/90). Folders are derived from paths and use shadcn Collapsible
// (nested, expand/collapse); files are links that load into the content pane.
// Live-updates arrive via the parent re-rendering with fresh entries.
//
// Folder open/close is CONTROLLED at the FileTree level: `openDirs` holds the
// full paths (e.g. "a/b") of the open folders. Folders start collapsed; the
// ancestor folders of the currently-open doc (`currentPath`) are seeded open so
// the active file is visible. Controlling the state (rather than per-folder
// `defaultOpen`) also drives the chevron rotation off each folder's OWN boolean,
// so a nested chevron no longer leaks its parent's open state.
//
// FileTree is shared across the `/v/:vaultId/*` route and is NOT remounted when
// the vault changes, so switching vaults RESETS `openDirs` to the new vault's
// open-doc ancestors — the previous vault's open folders never leak in. Within a
// single vault, navigation only unions ancestors (user-opened folders persist).

import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { IconChevronRight, IconFile, IconFolder } from "@tabler/icons-react";
import type { FileEntry } from "@cairn/core";
import { displayName, fileRoute } from "../wikiLinks";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface TreeDir {
  dirs: Map<string, TreeDir>;
  files: FileEntry[];
}

function buildTree(entries: FileEntry[]): TreeDir {
  const root: TreeDir = { dirs: new Map(), files: [] };
  for (const entry of entries) {
    const parts = entry.path.split("/");
    let node = root;
    for (const part of parts.slice(0, -1)) {
      let next = node.dirs.get(part);
      if (!next) {
        next = { dirs: new Map(), files: [] };
        node.dirs.set(part, next);
      }
      node = next;
    }
    node.files.push(entry);
  }
  return root;
}

function fileName(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}

function sortNames(names: string[]): string[] {
  return [...names].sort((a, b) => a.localeCompare(b));
}

// The ancestor folder paths of a file, from outermost in. For "a/b/c.md" this is
// ["a", "a/b"] (the filename is dropped, prefixes accumulate). Empty when there
// is no current doc, so nothing is force-opened.
function ancestorDirs(currentPath: string): string[] {
  if (!currentPath) return [];
  const parts = currentPath.split("/");
  parts.pop(); // drop the filename; only folders remain
  const out: string[] = [];
  let prefix = "";
  for (const part of parts) {
    prefix = prefix ? `${prefix}/${part}` : part;
    out.push(prefix);
  }
  return out;
}

function DirNode({
  name,
  path,
  dir,
  vaultId,
  currentPath,
  openDirs,
  onToggle,
}: {
  name: string;
  path: string;
  dir: TreeDir;
  vaultId: string;
  currentPath: string;
  openDirs: Set<string>;
  onToggle: (path: string) => void;
}) {
  const isOpen = openDirs.has(path);
  return (
    <Collapsible open={isOpen} onOpenChange={() => onToggle(path)}>
      <CollapsibleTrigger className="flex w-full items-center gap-1 rounded-4xl px-2 py-1 text-sm font-medium text-muted-foreground outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring">
        <IconChevronRight
          className={cn("size-3.5 shrink-0 transition-transform", isOpen && "rotate-90")}
        />
        <IconFolder className="size-4 shrink-0" />
        <span className="truncate">{name}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-3">
        <DirChildren
          dir={dir}
          path={path}
          vaultId={vaultId}
          currentPath={currentPath}
          openDirs={openDirs}
          onToggle={onToggle}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

function DirChildren({
  dir,
  path,
  vaultId,
  currentPath,
  openDirs,
  onToggle,
}: {
  dir: TreeDir;
  path: string;
  vaultId: string;
  currentPath: string;
  openDirs: Set<string>;
  onToggle: (path: string) => void;
}) {
  const files = [...dir.files].sort((a, b) => a.path.localeCompare(b.path));
  return (
    <ul className="flex flex-col gap-0.5">
      {sortNames([...dir.dirs.keys()]).map((name) => {
        const sub = dir.dirs.get(name);
        const childPath = path ? `${path}/${name}` : name;
        return sub ? (
          <li key={`d:${name}`}>
            <DirNode
              name={name}
              path={childPath}
              dir={sub}
              vaultId={vaultId}
              currentPath={currentPath}
              openDirs={openDirs}
              onToggle={onToggle}
            />
          </li>
        ) : null;
      })}
      {files.map((f) => (
        <li key={f.fileId}>
          <NavLink
            to={fileRoute(vaultId, f.path)}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-1.5 rounded-4xl px-2 py-1 pl-6 text-sm text-muted-foreground outline-none hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring",
                (isActive || f.path === currentPath) &&
                  "bg-primary/10 font-medium text-primary hover:bg-primary/10 hover:text-primary",
              )
            }
          >
            <IconFile className="size-3.5 shrink-0 opacity-70" />
            <span className="truncate">{displayName(fileName(f.path))}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

export function FileTree({
  vaultId,
  entries,
  currentPath,
}: {
  vaultId: string;
  entries: FileEntry[];
  currentPath: string;
}) {
  // Open folders, by full path. Seeded from the current doc's ancestors so the
  // active file is revealed; everything else starts collapsed. Seeding lives in
  // state (not derived from `entries`) so it survives entries loading async —
  // `entries` may be empty on first render, then populate, but the ancestors of
  // `currentPath` are already marked open.
  const [openDirs, setOpenDirs] = useState<Set<string>>(() => new Set(ancestorDirs(currentPath)));

  // The vault this tree's `openDirs` belongs to. FileTree is shared across the
  // `/v/:vaultId/*` route and is NOT remounted on vault switch, so we detect the
  // switch here rather than relying on a remount.
  const vaultRef = useRef(vaultId);

  // Keep `openDirs` in step with navigation. Two cases:
  //   • Same vault: union-in the new doc's ancestors so it becomes visible. We
  //     only ADD — folders the user opened or closed by hand are preserved, and
  //     navigating never auto-collapses anything.
  //   • Different vault: RESET to just the new doc's ancestors. The previous
  //     vault's open folders (and any matching paths) must not leak in; the new
  //     vault starts collapsed except the open doc's ancestors.
  useEffect(() => {
    const ancestors = ancestorDirs(currentPath);
    if (vaultRef.current !== vaultId) {
      vaultRef.current = vaultId;
      setOpenDirs(new Set(ancestors));
      return;
    }
    if (ancestors.length === 0) return;
    setOpenDirs((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const a of ancestors) {
        if (!next.has(a)) {
          next.add(a);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [vaultId, currentPath]);

  const toggle = useCallback((path: string) => {
    setOpenDirs((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const tree = buildTree(entries);
  if (entries.length === 0)
    return <p className="px-2 py-1 text-sm text-muted-foreground">Empty vault.</p>;
  return (
    <nav aria-label="Files">
      <DirChildren
        dir={tree}
        path=""
        vaultId={vaultId}
        currentPath={currentPath}
        openDirs={openDirs}
        onToggle={toggle}
      />
    </nav>
  );
}
